import React, { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { InputAdornment } from '@material-ui/core';

import { FormNumberField, TextFieldProps } from '../../forms';

/** Where editing an unset preferred temperature starts when no property default applies, in °F. */
export const DEFAULT_PREFERRED_TEMPERATURE = 70;

/** The range a guest can set, in °F; the reservation API enforces the same limits. */
export const MIN_PREFERRED_TEMPERATURE = 60;
export const MAX_PREFERRED_TEMPERATURE = 80;

const validationOptions = {
  min: {
    value: MIN_PREFERRED_TEMPERATURE,
    message: `Minimum temperature is ${MIN_PREFERRED_TEMPERATURE}°F.`,
  },
  max: {
    value: MAX_PREFERRED_TEMPERATURE,
    message: `Maximum temperature is ${MAX_PREFERRED_TEMPERATURE}°F.`,
  },
};

interface Props {
  name: string;
  preferredTemperature?: number | null;
  inputProps?: TextFieldProps;
}

/**
 * Preferred room temperature on a profile form. An unset preference stays empty, so saving the rest of the
 * profile keeps "no preference"; the field starts at 70°F once the user focuses it. Whole degrees from 60°F to
 * 80°F, since the thermostat display shows whole degrees.
 */
export const PreferredTemperatureField: React.FC<Props> = ({
  name,
  preferredTemperature,
  inputProps,
}) => {
  const { getValues, setValue } = useFormContext();

  const startAtDefault = useCallback(() => {
    const value = getValues(name);
    if (value === '' || value == null) setValue(name, DEFAULT_PREFERRED_TEMPERATURE);
  }, [getValues, setValue, name]);

  return (
    <FormNumberField
      {...inputProps}
      name={name}
      label="Preferred Room Temperature"
      fullWidth
      integer
      defaultValue={preferredTemperature ?? ''}
      validationOptions={validationOptions}
      inputProps={{
        ...inputProps?.inputProps,
        inputMode: 'numeric',
        step: 1,
        min: MIN_PREFERRED_TEMPERATURE,
        max: MAX_PREFERRED_TEMPERATURE,
        onFocus: startAtDefault,
      }}
      InputProps={{
        ...inputProps?.InputProps,
        endAdornment: <InputAdornment position="end">°F</InputAdornment>,
      }}
    />
  );
};
